import React from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import cx from 'classnames';
import { pick, omit } from 'helpers/object';
import { LocalizableMessageShape } from 'components/LocalizableMessage/LocalizableMessage';
import { Location } from './Items/Location';
import { SearchBoxNoResults } from './Items/SearchBoxNoResults';
import { SearchBoxField } from './SearchBoxField';
import { getItemNormalizer } from './SearchBox.helpers';
import { LOCATION_TYPES } from './SearchBox.constants';
import { PanelSection } from './PanelSection';
import { Loader } from './Loader';
import css from './SearchBox.scss';

const itemToString = i => ((i && i.locationName) || '').replace(/<[^>]*>/g, '');
const locationIconPath = require('static/design-system/icons/pin.svg');

const messages = defineMessages({
  noMatches: {
    id: 'searchbox.item.noMatches',
    defaultMessage: 'No matches',
  },
  placeholder: {
    id: 'searchbox.locationField.placeholder',
    defaultMessage: 'City, neighborhood',
  },
  searchPrompt: {
    id: 'searchbox.item.searchPrompt',
    defaultMessage: 'Try searching by city or neighborhood',
  },
});

export const LocationFieldUnwrapped = props => {
  const { intl } = props;
  return (
    <SearchBoxField
      {...pick(Object.keys(SearchBoxField.propTypes), props)}
      itemToString={itemToString}
      isClearable={false}
      icon={<Isvg src={locationIconPath} />}
      placeholder={intl.formatMessage(messages.placeholder)}
      blurredValue={
        typeof props.blurredValue === 'object' ? intl.formatMessage(props.blurredValue) : props.blurredValue
      }>
      {({ getItemProps, highlightedIndex, inputValue, onPanelScroll }) => {
        const { history, intl: internationalize, isLoading, onRemoveItem, suggestions } = props;
        const [items, itemType] = inputValue
          ? [suggestions, LOCATION_TYPES.autocomplete]
          : [history, LOCATION_TYPES.history];
        const normalizer = getItemNormalizer(itemType);
        const hasResults = items && items.length > 0;

        let sectionItems;
        if (isLoading) {
          sectionItems = <Loader />;
        } else if (hasResults) {
          sectionItems = items.map((item, index) => {
            const normalizedItem = normalizer(item);
            return (
              <Location
                {...getItemProps({
                  item: normalizedItem,
                  key: normalizedItem.place_id,
                  searchId: normalizedItem.location_id,
                  highlighted: index === highlightedIndex,
                  onRemoveClick: onRemoveItem,
                })}
                item={normalizedItem}
              />
            );
          });
        } else {
          const noResultsMessage = inputValue
            ? internationalize.formatMessage(messages.noMatches)
            : internationalize.formatMessage(messages.searchPrompt);
          sectionItems = <SearchBoxNoResults message={noResultsMessage} />;
        }

        return (
          <div data-qa="LocationField" className={cx(css.searchbox__panel)} onScroll={onPanelScroll}>
            <div data-qa="LocationField.content" className={css.content}>
              <PanelSection>{sectionItems}</PanelSection>
            </div>
          </div>
        );
      }}
    </SearchBoxField>
  );
};

LocationFieldUnwrapped.propTypes = {
  blurredValue: LocalizableMessageShape,
  isLoading: PropTypes.bool.isRequired,
  onRemoveItem: PropTypes.func.isRequired,
  history: PropTypes.array,
  suggestions: PropTypes.array,
  ...omit(['prompt', 'itemToString', 'children', 'blurredValue', 'placeholder'], SearchBoxField.propTypes),
};

export const LocationField = injectIntl(LocationFieldUnwrapped);
